import { PresentationResource } from 'api/lessonsApi';
import { escapeHtml } from 'components/lessons/lessonsHelpers';
import { onEnterOrSpace } from 'components/subjectSite/subjectSiteMenu';
import React, { useEffect, useRef, useState } from 'react';
import SwiperCore, { Keyboard, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.min.css';
import { createAudioPlayers, MediaElementPlayer } from './mediaElement';

SwiperCore.use([Keyboard, Navigation]);

type Props = {
  slides: PresentationResource[];
};

/**
 * An audio element as HTML, so React leaves it alone once MediaElement.js has moved it into its
 * player
 */
const audioHtml = (src: string) => ({
  __html: `<audio style="width: 100%; max-width: 100%;"><source src="${escapeHtml(
    src
  )}" /></audio>`,
});

const slide = (resource: PresentationResource) => {
  if (!resource.videoURL) return null;

  switch (resource.resourceType) {
    case 'Image':
      return resource.additionalAudioURL ? (
        <div className="ImgAudioBlock">
          <div
            className="Audio"
            dangerouslySetInnerHTML={audioHtml(resource.additionalAudioURL)}
          />
          <div className="Img">
            <img src={resource.videoURL} alt={resource.name} />
          </div>
        </div>
      ) : (
        <img src={resource.videoURL} alt={resource.name} />
      );
    case 'Video':
      return (
        <div className="VidContainer">
          <video
            controls
            disablePictureInPicture
            controlsList="nodownload noplaybackrate"
            src={resource.videoURL}
          />
        </div>
      );
    case 'Audio':
      return <div dangerouslySetInnerHTML={audioHtml(resource.videoURL)} />;
    default:
      return null;
  }
};

/**
 * A slide show of images, videos and audio, moved by the arrows or the keyboard, starting full
 * screen
 */
const Presentation: React.FC<Props> = ({ slides }) => {
  const blockRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<SwiperCore | null>(null);
  const [bFullScreen, setBFullScreen] = useState(true);

  useEffect(() => {
    swiperRef.current?.update();
  }, [bFullScreen]);

  useEffect(() => {
    let players: MediaElementPlayer[] = [];
    let bLeft = false;
    const audios = Array.from(
      blockRef.current?.querySelectorAll('audio') ?? []
    );

    if (audios.length > 0) {
      createAudioPlayers(audios, (created) => {
        players = created;
        if (bLeft) players.forEach((player) => player.remove());
      });
    }

    return () => {
      bLeft = true;
      players.forEach((player) => player.remove());
    };
  }, [slides]);

  // Media on the slide left behind stops and goes back to the start
  const stopMedia = () => {
    blockRef.current
      ?.querySelectorAll<HTMLMediaElement>('video, audio')
      .forEach((media) => {
        media.pause();
        media.currentTime = 0;
      });
  };

  const toggleFullScreen = () => setBFullScreen((previous) => !previous);

  return (
    <div className="PresentationBlock" ref={blockRef}>
      <div className="IEUnavailableMsg">
        <h3>
          Presentation not available on IE, please try on another browser.
        </h3>
      </div>

      <div className={`PBContainer${bFullScreen ? ' FullScreen' : ''}`}>
        <Swiper
          id="presentation-swiper"
          speed={0}
          slidesPerView="auto"
          autoHeight={true}
          simulateTouch={false}
          keyboard={{ enabled: true }}
          navigation={{
            prevEl: '.PresentationBlock .BtmCtrls .PrevBtn',
            nextEl: '.PresentationBlock .BtmCtrls .NextBtn',
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onTransitionStart={stopMedia}
        >
          {slides.map((resource) => (
            <SwiperSlide key={resource.resourcesId}>
              {slide(resource)}
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="BtmCtrlsContainer">
          <div className="BtmCtrls">
            <div className="Inner">
              <div className="PrevBtn"></div>
              <div className="BtnSpacer"></div>
              <div className="NextBtn"></div>
              <div className="RightSide">
                <div
                  className="FullScreenBtn"
                  role="button"
                  tabIndex={0}
                  aria-label="Full screen"
                  onClick={toggleFullScreen}
                  onKeyDown={onEnterOrSpace(toggleFullScreen)}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Presentation;
